const melemap_lib = require('../../../meleMap/meleMap_lib.js')

// Default tile buffer size (in tiles)
const DEFAULT_TILEBUFFER = 5

/**
 * TileManager - Manages basemap and tile overlay layers
 *
 * Builds Leaflet GridLayers that load tiles from msysProxy in batches.
 * Tiles requested within the coalescing window (see meleMap_lib) are merged
 * into one mmFilesGet call, so the chain patrol -> mBox -> server uses one
 * roundtrip per batch instead of one per tile.
 */
export default class TileManager {
  /**
   * Create a new TileManager
   * @param {L.Map} leafletMap - The Leaflet map instance
   * @param {Object} [options={}]
   * @param {number} [options.maxzoom=19]
   * @param {number} [options.tilebuffer=5]
   * @param {object} [options.listener] - msysListener used by the batched tile layer to call msysProxy
   * @param {string} [options.format='png'] - default tile file extension for the basemap and overlays
   * @param {Function} [options.warnFn]
   */
  constructor(leafletMap, options = {}) {
    this._map = leafletMap
    this._maxzoom = options.maxzoom || 19
    this._tilebuffer = options.tilebuffer || DEFAULT_TILEBUFFER
    this._listener = options.listener || null
    this._defaultFormat = options.format || 'png'
    this._warn = options.warnFn || console.warn

    this._tileOverlays = {}
    this._currentBasemapLayer = null
    this._basemap = null
    this._basemapFormat = this._defaultFormat
  }

  /**
   * Set the basemap repository (and optionally a non-default file format)
   * @param {string} basemap - Repository name (e.g. 'map_osm')
   * @param {string} [format] - Tile file extension (defaults to the manager's default format)
   */
  setBasemap(basemap, format) {
    this._basemap = basemap
    if (format) this._basemapFormat = format
    this._renderBasemap()
  }

  getBasemap() {
    return this._basemap
  }

  setMaxZoom(zoom) {
    this._maxzoom = zoom
  }

  _renderBasemap() {
    if (this._currentBasemapLayer) {
      this._map.removeLayer(this._currentBasemapLayer)
      this._currentBasemapLayer = null
    }

    if (!this._basemap) {
      this._warn('Basemap is not set, cannot render map')
      return
    }
    if (!this._listener) {
      this._warn('TileManager has no msysListener; cannot render basemap')
      return
    }

    this._currentBasemapLayer = this._createBatchedLayer(this._basemap, this._basemapFormat, 0)
    if (this._currentBasemapLayer) this._currentBasemapLayer.addTo(this._map)
  }

  /**
   * Reload/refresh the basemap and all tile overlays
   */
  reload() {
    if (!this._map) return
    this._renderBasemap()

    Object.values(this._tileOverlays).forEach(overlay => {
      if (overlay.layer) {
        this._map.removeLayer(overlay.layer)
        overlay.layer.addTo(this._map)
      }
    })
  }

  _createBatchedLayer(repo, format, zIndex) {
    if (!repo || !this._listener) return null
    return melemap_lib.createBatchedProxyTileLayer({
      listener: this._listener,
      repo,
      format: format || this._defaultFormat,
      zIndex,
      edgeBufferTiles: this._tilebuffer,
    })
  }

  /**
   * Add or update a tile overlay layer
   *
   * @param {string} id - Overlay identifier (e.g., 'roads', 'railways')
   * @param {Object} [cfg={}]
   * @param {string} cfg.url - Repository name
   * @param {string} [cfg.format] - Tile file extension (defaults to manager's default format)
   * @param {number} [cfg.zindex=400] - Stacking order
   */
  setTileOverlay(id, cfg = {}) {
    if (this._tileOverlays[id]) {
      this.removeTileOverlay(id)
    }
    const repo = cfg.url
    const format = cfg.format || this._defaultFormat
    const zIndex = cfg.zindex || 400

    const layer = this._createBatchedLayer(repo, format, zIndex)
    this._tileOverlays[id] = { layer, url: repo, format, zindex: zIndex }
    if (layer) layer.addTo(this._map)
  }

  removeTileOverlay(id) {
    const overlay = this._tileOverlays[id]
    if (!overlay) return
    if (overlay.layer) this._map.removeLayer(overlay.layer)
    delete this._tileOverlays[id]
  }

  getTileOverlays() {
    return Object.keys(this._tileOverlays).map(id => ({
      id,
      url: this._tileOverlays[id].url,
      format: this._tileOverlays[id].format,
      zindex: this._tileOverlays[id].zindex,
    }))
  }

  destroy() {
    if (this._currentBasemapLayer) {
      this._map.removeLayer(this._currentBasemapLayer)
      this._currentBasemapLayer = null
    }
    Object.keys(this._tileOverlays).forEach(id => {
      this.removeTileOverlay(id)
    })
  }
}
